'use client';

import { Portal } from '@common/components';
import { SvgChevronleft } from '@common/icon';
import { cn } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import root from 'react-shadow';

import { TERM_CATEGORY, UnauthQueryOptions } from '@/api';
import { ButtonFooter } from '@/components/server';
import { useModalClosing } from '@/hooks/useModalClosing';
import { jejuYouthDreamQueryOptions } from '@/query-factory/jeju-youthdream';

interface KosafTermProps {
  opened?: boolean;
  onClose: VoidFunction;
  termType?:
    | 'privacy_agreement'
    | 'consulting_agreement'
    | 'collection_agreement'
    | 'provide_agreement';
  onConfirmationClick: VoidFunction;
}

export const JejuTerm = ({
  opened = false,
  onClose,
  termType = 'privacy_agreement',
  onConfirmationClick,
}: KosafTermProps) => {
  const openingTransition = useModalClosing({
    opened,
    closingDuration: 200,
  });

  const termList = useQuery(jejuYouthDreamQueryOptions.termList());
  const term = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/term/{termCategory}']({
      path: {
        termCategory: TERM_CATEGORY.JEJU_PROVIDE_PRIVACY_SELECT,
      },
    }),
  });

  const title = (() => {
    switch (termType) {
      case 'provide_agreement':
        return '개인정보 제3자 제공 동의 (제주청년센터)';

      case 'privacy_agreement':
        return '개인정보처리방침 동의';

      case 'consulting_agreement':
        return '청년이어드림 프로그램 참여 동의';

      case 'collection_agreement':
        return '상담녹화 및 민감정보 수집·이용에 대한 동의';

      default:
        return '개인정보처리방침 동의';
    }
  })();

  const handleAgreeClick = () => {
    onClose();
    onConfirmationClick();
  };

  const contentsHtml = {
    __html:
      termType === 'provide_agreement'
        ? term.data?.context?.contents || ''
        : termList.data?.find((term) => term.termsId === termType)
            ?.termsContent || '',
  };

  return openingTransition ? (
    <Portal renderTo={() => document.body}>
      <div className="z-51 fixed left-0 top-0 flex size-full items-end justify-center">
        <div className="relative flex size-full max-w-root items-end justify-end">
          <article
            className={cn(
              '[&>section]:p-24 [&>section]:px-20',
              'z-51 fixed flex size-full max-h-screen min-h-screen flex-col bg-Gray-white',
            )}
          >
            <nav className="box-border grid min-h-56 grid-cols-2 items-center bg-Gray-white px-10 text-center">
              <div className="flex">
                <button
                  aria-label="뒤로 가기"
                  className="relative size-48 flex-center"
                  type="button"
                  onClick={onClose}
                >
                  <SvgChevronleft className="size-24" />
                </button>
              </div>
              <h3 className="font-semibold absolute left-1/2 flex w-auto -translate-x-1/2 items-center justify-center break-keep text-center text-[1rem] text-Gray-900">
                {title}
              </h3>
            </nav>
            <root.section className="overflow-auto" title={title}>
              <div
                className="z-51 size-full border-none"
                dangerouslySetInnerHTML={contentsHtml}
              />
            </root.section>
            <ButtonFooter onClick={handleAgreeClick}>동의</ButtonFooter>
          </article>
        </div>
      </div>
    </Portal>
  ) : (
    <></>
  );
};
